import React from "react";
import "./Button.css";

/**
 * Base Button Component
 * 
 * A flexible, reusable button component that supports multiple variants, sizes, 
 * loading states, icons, and accessibility features.
 * 
 * @param {Object} props
 * @param {ReactNode} props.children - Button content
 * @param {string} props.variant - Button style variant (primary, gray, danger, success, outline-blue, outline-gray, outline-red, link)
 * @param {string} props.size - Button size (sm, md, lg)
 * @param {boolean} props.loading - Show loading spinner
 * @param {string} props.loadingText - Text to show when loading
 * @param {boolean} props.disabled - Disable button
 * @param {boolean} props.fullWidth - Take full width of container
 * @param {ReactNode} props.icon - Icon element
 * @param {string} props.iconPosition - Icon position (left, right)
 * @param {boolean} props.iconOnly - Icon-only button (no text)
 * @param {function} props.onClick - Click handler
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Inline styles (use sparingly)
 */
const Button = ({
  children,
  variant = "primary",
  size = "md",
  loading = false,
  loadingText = "Loading...",
  disabled = false,
  fullWidth = false,
  icon = null,
  iconPosition = "left",
  iconOnly = false,
  onClick,
  type = "button",
  className = "",
  style = {},
  ariaLabel,
  title,
  ...props
}) => {
  const classNames = [
    "app-btn",
    `app-btn-${variant}`,
    `app-btn-${size}`,
    fullWidth && "app-btn-full-width",
    loading && "app-btn-loading",
    iconOnly && "app-btn-icon-only",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = (e) => {
    if (!loading && !disabled && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      className={classNames}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={ariaLabel || (typeof children === "string" ? children : undefined)}
      title={title}
      style={style}
      {...props}
    >
      {loading ? (
        <>
          <span className="app-btn-spinner"></span>
          <span className="app-btn-content">{loadingText}</span>
        </>
      ) : (
        <>
          {icon && iconPosition === "left" && !iconOnly && (
            <span className="app-btn-icon-left">{icon}</span>
          )}
          {iconOnly ? icon : <span className="app-btn-content">{children}</span>}
          {icon && iconPosition === "right" && !iconOnly && (
            <span className="app-btn-icon-right">{icon}</span>
          )}
        </>
      )}
    </button>
  );
};

export default Button;

